import { Link, useLocation, useNavigate } from 'react-router-dom'
import EmptyState from '@/components/shared/EmptyState'
import { formatCurrency, formatDate } from '@/lib/utils'
import { IconFileText, IconEye, IconEdit, IconPlus } from '@/components/icons/Icons'
import type { Cotizacion } from '@/lib/types'

interface TabCotizacionesProps {
  cotizaciones: Cotizacion[]
  empresaId: number
}

const estadoBadge: Record<string, string> = {
  borrador: 'badge-borrador',
  enviada: 'badge-enviada',
  aceptada: 'badge-aceptada',
  rechazada: 'badge-rechazada',
  en_negociacion: 'badge-enviada',
}

const estadoLabel: Record<string, string> = {
  borrador: 'Borrador',
  enviada: 'Enviada',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  en_negociacion: 'Negociacion',
}

export default function TabCotizaciones({ cotizaciones, empresaId }: TabCotizacionesProps) {
  const navigate = useNavigate()
  /* El editor de cotizaciones es del ERP: el partner todavia no crea ni edita
     documentos, los lee por su enlace publico igual que su cliente. */
  const enPartner = useLocation().pathname.startsWith('/partner')

  if (cotizaciones.length === 0) {
    return (
      <EmptyState
        icon={<IconFileText size={40} />}
        title="Sin cotizaciones"
        description="Esta empresa aun no tiene cotizaciones registradas."
        actionLabel={enPartner ? undefined : 'Nueva Cotizacion'}
        onAction={enPartner ? undefined : () => navigate(`/cotizaciones/nueva?cliente_id=${empresaId}`)}
      />
    )
  }

  return (
    <div>
      {!enPartner && (
        <div className="empresa-tab-actions">
          <Link to={`/cotizaciones/nueva?cliente_id=${empresaId}`} className="btn btn-primary btn-sm">
            <IconPlus size={14} />
            Nueva Cotizacion
          </Link>
        </div>
      )}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>N</th>
              <th>Proyecto</th>
              <th>Fecha</th>
              <th>Total</th>
              <th>Estado</th>
              <th style={{ width: 100 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cotizaciones.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.numero}</td>
                <td>{c.proyecto_nombre || '---'}</td>
                <td>{formatDate(c.fecha_emision || c.created_at)}</td>
                <td style={{ fontWeight: 700 }}>{formatCurrency(c.total, c.moneda)}</td>
                <td>
                  <span className={`badge ${estadoBadge[c.estado] || ''}`}>
                    {estadoLabel[c.estado] || c.estado}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {enPartner ? (
                      c.public_token && (
                        <a href={`/ver/${c.public_token}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" title="Ver documento">
                          <IconEye size={14} />
                        </a>
                      )
                    ) : (
                      <>
                        <Link to={`/cotizaciones/${c.id}`} className="btn btn-sm btn-secondary" title="Ver">
                          <IconEye size={14} />
                        </Link>
                        <Link to={`/cotizaciones/${c.id}/editar`} className="btn btn-sm btn-secondary" title="Editar">
                          <IconEdit size={14} />
                        </Link>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
